import { twistyButtonExpandConversationLabel } from './MailListItemTwistyButton.locstring.json';
import styles from './MailListItemTwistyButton.scss';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import classnamesBind from 'classnames/bind';
import { observer } from 'mobx-react-lite';
import { logUsage } from 'owa-analytics';
import { ControlIcons } from 'owa-control-icons';
import { getDensityModeString } from 'owa-fabric-theme';
import { isFeatureEnabled } from 'owa-feature-flags';
import ChevronRightRegular from 'owa-fluent-icons-svg/lib/icons/ChevronRightRegular';
import { FluentButton } from 'owa-fluent-v9-shims';
import loc, { isCurrentCultureRightToLeft } from 'owa-localize';
import { MailIcons } from 'owa-mail-icons';
import { onMailListItemClickHandler } from 'owa-mail-list-item-shared';
import listViewStore from 'owa-mail-list-store/lib/store/Store';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import shouldShowUnstackedReadingPane from 'owa-mail-store/lib/utils/shouldShowUnstackedReadingPane';
import { touchHandler } from 'owa-touch-handler';
import * as React from 'react';

const classNames = classnamesBind.bind(styles);

export interface MailListItemTwistyButtonProps {
    isLoadingExpansion: boolean;
    isSecondLevelExpanded: boolean;
    isSelected: boolean;
    isSingleLine: boolean;
    isUnread: boolean;
    rowKey: string;
    shouldShowTwisty: boolean;
    showCondensedView: boolean;
    tableViewId: string;
}

export default observer(function MailListItemTwistyButton(props: MailListItemTwistyButtonProps) {
    const {
        isLoadingExpansion,
        isSecondLevelExpanded,
        isSelected,
        isSingleLine,
        isUnread,
        rowKey,
        shouldShowTwisty,
        showCondensedView,
        tableViewId,
    } = props;

    // unneeded in spinner case, but hooks must be defined prior to any return statements
    const onClick = React.useCallback(
        (evt: React.MouseEvent<unknown>) => {
            onMailListItemClickHandler(
                evt,
                MailListItemSelectionSource.MailListItemTwisty,
                rowKey,
                tableViewId
            );

            logUsage('MailListRowExpanded', [
                showCondensedView,
                listViewStore.tableViews.get(tableViewId)?.tableQuery.type,
            ]);
        },
        [rowKey, tableViewId, showCondensedView]
    );

    // show spinner if loading expansion
    if (isLoadingExpansion) {
        return (
            <Spinner
                // need to stop the event from bubbling up and causing the popout view
                onDoubleClick={stopPropagation}
                className={
                    isSingleLine
                        ? styles.loadingItemPartsSpinnerSingleLine
                        : styles.loadingItemPartsSpinnerThreeColumn
                }
                size={SpinnerSize.xSmall}
            />
        );
    }

    const hasHighTwisty = !isSingleLine && isFeatureEnabled('mon-tri-mailItemTwisty');
    const densityModeString = getDensityModeString();

    const iconButtonClasses = classNames(
        isSingleLine
            ? styles.twistySingleLine
            : classNames(
                  styles.twistyThreeColumn,
                  densityModeString,
                  hasHighTwisty && styles.highTwistyIconButton,
                  hasHighTwisty && !shouldShowTwisty && styles.hideTwisty,
                  hasHighTwisty && isSecondLevelExpanded && styles.secondLevelExpansion,
                  shouldShowUnstackedReadingPane() && styles.unstackedConversation,
                  isFeatureEnabled('mon-densities') && styles.iconsNext
              ),
        isUnread && styles.twistyUnread,
        isSelected && styles.isMessageSelected
    );

    const iconRootStyles = classNames(
        styles.twistyImage,
        isSecondLevelExpanded &&
            (isCurrentCultureRightToLeft() ? styles.expandedrtl : styles.expandedltr),
        isFeatureEnabled('mon-densities') && styles.iconsNext,
        densityModeString
    );

    return (
        <div
            className={classNames(hasHighTwisty && styles.highTwistyIconButton)}
            {...touchHandler({ onClick: onClick })}>
            <FluentButton
                appearance="icon"
                aria-expanded={isSecondLevelExpanded}
                aria-label={loc(twistyButtonExpandConversationLabel)}
                className={classNames(iconButtonClasses, styles.button, 'flipForRtl')}
                iconProps={{
                    iconName: isFeatureEnabled('mon-densities')
                        ? ChevronRightRegular
                        : hasHighTwisty
                        ? MailIcons.ChevronRightSmall
                        : ControlIcons.ChevronRightMed,
                    styles: {
                        root: iconRootStyles,
                    },
                }}
                tabIndex={-1}
                onClick={onClick}
                onDoubleClick={stopPropagation}
            />
        </div>
    );
});

const stopPropagation = (evt: React.MouseEvent<unknown>) => {
    evt.stopPropagation();
};
